import React, { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Bot, ShieldCheck, Mail, Send, KeyRound, MessageCircle, LogOut, RefreshCw, MapPin } from "lucide-react";
import api, { formatApiError } from "@/services/api";
import { useLang } from "@/context/LanguageContext";
import { parseDecimalInput, isDecimalTypingValue } from "@/utils/decimal";
import { DEFAULT_FEE_PERCENT, feePercentFromSettings } from "@/utils/serviceFee";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

export default function SettingsPage() {
  const { t } = useLang();
  const qc = useQueryClient();

  const { data: me, refetch: refetchMe } = useQuery({
    queryKey: ["auth-me"],
    queryFn: async () => (await api.get("/auth/me")).data,
  });
  const { data: zelle, isLoading: zL } = useQuery({
    queryKey: ["zelle"],
    queryFn: async () => (await api.get("/zelle")).data,
  });
  const { data: settings, isLoading: sL } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/settings")).data,
  });

  const [zForm, setZForm] = useState({ name: "", email: "", phone: "", whatsapp: "", instructions: "" });
  const [zelleHandle, setZelleHandle] = useState(""); // nimewo OSWA email Zelle
  const [sForm, setSForm] = useState({
    service_fee_percent: DEFAULT_FEE_PERCENT,
    agent_bot_token: "",
    admin_notif_bot_token: "",
    admin_mgmt_bot_token: "",
    admin_chat_ids: "",
    public_base_url: "",
    resend_api_key: "",
    resend_from_email: "",
    notify_emails: "",
    whatsapp_enabled: true,
    whatsapp_service_url: "",
    whatsapp_bridge_secret: "",
  });
  const [waTestTo, setWaTestTo] = useState("");
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [totpSetup, setTotpSetup] = useState(null);
  const [totpCode, setTotpCode] = useState("");
  const [totpDisable, setTotpDisable] = useState({ password: "", code: "" });

  useEffect(() => {
    if (zelle) {
      setZForm(zelle);
      setZelleHandle((zelle.email || zelle.phone || "").trim());
    }
  }, [zelle]);
  useEffect(() => {
    if (settings) {
      setSForm({
        service_fee_percent: settings.service_fee_percent ?? settings.service_fee_per_100 ?? DEFAULT_FEE_PERCENT,
        agent_bot_token: settings.agent_bot_token || settings.telegram_bot_token || "",
        admin_notif_bot_token: settings.admin_notif_bot_token || "",
        admin_mgmt_bot_token: settings.admin_mgmt_bot_token || "",
        admin_chat_ids: settings.admin_chat_ids || "",
        public_base_url: settings.public_base_url || "",
        resend_api_key: settings.resend_api_key || "",
        resend_from_email: settings.resend_from_email || "",
        notify_emails: settings.notify_emails || "",
        whatsapp_enabled: settings.whatsapp_enabled !== false,
        whatsapp_service_url: settings.whatsapp_service_url || "",
        whatsapp_bridge_secret: settings.whatsapp_bridge_secret || "",
      });
    }
  }, [settings]);

  const saveZ = useMutation({
    mutationFn: async () => {
      const h = zelleHandle.trim();
      const isEmail = h.includes("@");
      const payload = {
        ...zForm,
        email: isEmail ? h : "",
        phone: isEmail ? "" : h,
      };
      return (await api.put("/zelle", payload)).data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["zelle"] });
      if (data?.clients_notified) {
        toast.success("Enfòmasyon Zelle sove — kliyan yo ap resevwa mesaj WhatsApp otomatik");
      } else if (data?.notify_detail) {
        toast.success("Enfòmasyon Zelle sove");
        toast.error(data.notify_detail);
      } else {
        toast.success("Enfòmasyon Zelle sove");
      }
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const saveS = useMutation({
    mutationFn: async () => {
      const fee = Math.round(parseDecimalInput(sForm.service_fee_percent) * 10000) / 10000;
      return (await api.put("/settings", {
        ...sForm,
        service_fee_percent: fee,
        telegram_bot_token: sForm.agent_bot_token || "",
      })).data;
    },
    onSuccess: () => { toast.success("Paramèt sove"); qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const testEmail = useMutation({
    mutationFn: async () => (await api.post("/settings/test-email")).data,
    onSuccess: () => toast.success("Imèl tès voye! Tcheke bwat resepsyon w."),
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const testTg = useMutation({
    mutationFn: async () => (await api.post("/telegram/test-notify")).data,
    onSuccess: () => toast.success("Mesaj tès Telegram voye — tcheke chat ou"),
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const { data: waStatus, refetch: refetchWa } = useQuery({
    queryKey: ["whatsapp-status"],
    queryFn: async () => (await api.get("/whatsapp/status")).data,
    refetchInterval: (q) => (q.state.data?.state === "qr" ? 2500 : 15000),
    retry: false,
  });

  const saveWa = useMutation({
    mutationFn: async () => (await api.put("/settings", {
      whatsapp_enabled: true,
      whatsapp_service_url: (sForm.whatsapp_service_url || "").trim().replace(/\/+$/, ""),
      whatsapp_bridge_secret: (sForm.whatsapp_bridge_secret || "").trim(),
    })).data,
    onSuccess: () => {
      toast.success("WhatsApp ajan aktive — ap chaje estati…");
      qc.invalidateQueries({ queryKey: ["settings"] });
      setTimeout(() => refetchWa(), 800);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const waLogout = useMutation({
    mutationFn: async () => (await api.post("/whatsapp/logout")).data,
    onSuccess: () => { toast.success("WhatsApp dekonekte — eskane nouvo QR"); refetchWa(); },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const waTest = useMutation({
    mutationFn: async () => (await api.post("/whatsapp/test-send", { to: waTestTo.trim() })).data,
    onSuccess: () => toast.success("Mesaj tès WhatsApp voye"),
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const changePassword = useMutation({
    mutationFn: async () => (await api.post("/auth/change-password", {
      current_password: pwForm.current,
      new_password: pwForm.newPw,
    })).data,
    onSuccess: () => {
      toast.success("Modpas chanje — sove nan MongoDB");
      setPwForm({ current: "", newPw: "", confirm: "" });
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const startTotp = useMutation({
    mutationFn: async () => (await api.post("/auth/totp/setup")).data,
    onSuccess: (data) => {
      setTotpSetup(data);
      setTotpCode("");
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const confirmTotp = useMutation({
    mutationFn: async () => (await api.post("/auth/totp/confirm", { code: totpCode.trim() })).data,
    onSuccess: () => {
      toast.success("Otantifikatè aktif");
      setTotpSetup(null);
      setTotpCode("");
      refetchMe();
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const disableTotp = useMutation({
    mutationFn: async () => (await api.post("/auth/totp/disable", {
      current_password: totpDisable.password,
      code: totpDisable.code.trim(),
    })).data,
    onSuccess: () => {
      toast.success("Otantifikatè dezaktive");
      setTotpDisable({ password: "", code: "" });
      setTotpSetup(null);
      refetchMe();
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const submitPasswordChange = () => {
    if (pwForm.newPw.length < 8) {
      toast.error("Nouvo modpas la dwe gen omwen 8 karaktè");
      return;
    }
    if (pwForm.newPw !== pwForm.confirm) {
      toast.error("Modpas yo pa matche");
      return;
    }
    changePassword.mutate();
  };

  const [clientMsgOpen, setClientMsgOpen] = useState(false);
  const feePercent = feePercentFromSettings(settings);

  return (
    <div className="space-y-8 w-full max-w-4xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold">{t("settings")}</h1>
          <p className="text-sm text-muted-foreground mt-1">Konfigire kont Zelle, frè, bot Telegram (token + chat ID), ak imèl</p>
        </div>
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          onClick={() => setClientMsgOpen(true)}
          data-testid="message-clients-btn"
        >
          <MessageCircle className="h-4 w-4" /> Ekri kliyan TGS
        </Button>
      </motion.div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-brand-blue" />
          <h2 className="font-heading text-lg font-semibold">{t("change_password_title")}</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">{t("change_password_sub")}</p>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label htmlFor="admin-current-pw">{t("current_password")}</Label>
            <Input
              id="admin-current-pw"
              type="password"
              autoComplete="current-password"
              value={pwForm.current}
              onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })}
              data-testid="admin-current-password"
            />
          </div>
          <div>
            <Label htmlFor="admin-new-pw">{t("new_password")}</Label>
            <Input
              id="admin-new-pw"
              type="password"
              autoComplete="new-password"
              value={pwForm.newPw}
              onChange={(e) => setPwForm({ ...pwForm, newPw: e.target.value })}
              data-testid="admin-new-password"
            />
          </div>
          <div>
            <Label htmlFor="admin-confirm-pw">{t("confirm_password")}</Label>
            <Input
              id="admin-confirm-pw"
              type="password"
              autoComplete="new-password"
              value={pwForm.confirm}
              onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })}
              data-testid="admin-confirm-password"
            />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button
              className="bg-brand-blue hover:bg-brand-blueHover text-white"
              onClick={submitPasswordChange}
              disabled={changePassword.isPending || !pwForm.current || !pwForm.newPw}
              data-testid="save-admin-password-btn"
            >
              {changePassword.isPending ? "Ap sove..." : t("save_password")}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="admin-totp-card">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-brand-blue" />
          <h2 className="font-heading text-lg font-semibold">Otantifikatè admin</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Apre modpas la, koneksyon an mande yon kòd 6 chif nan Google Authenticator, Authy, oswa nenpòt aplikasyon otantifikatè.
        </p>
        {me?.totp_enabled ? (
          <div className="space-y-3">
            <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Otantifikatè a aktif.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="totp-disable-pw">Modpas aktyèl</Label>
                <Input
                  id="totp-disable-pw"
                  type="password"
                  autoComplete="current-password"
                  value={totpDisable.password}
                  onChange={(e) => setTotpDisable({ ...totpDisable, password: e.target.value })}
                  data-testid="totp-disable-password"
                />
              </div>
              <div>
                <Label htmlFor="totp-disable-code">Kòd otantifikatè</Label>
                <Input
                  id="totp-disable-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={totpDisable.code}
                  onChange={(e) => setTotpDisable({ ...totpDisable, code: e.target.value })}
                  placeholder="6 chif"
                  data-testid="totp-disable-code"
                />
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={disableTotp.isPending || !totpDisable.password || totpDisable.code.trim().length < 6}
              onClick={() => disableTotp.mutate()}
              data-testid="totp-disable-btn"
            >
              {disableTotp.isPending ? "Ap retire..." : "Dezaktive otantifikatè a"}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {!totpSetup ? (
              <Button
                type="button"
                className="bg-brand-blue hover:bg-brand-blueHover text-white"
                disabled={startTotp.isPending}
                onClick={() => startTotp.mutate()}
                data-testid="totp-setup-btn"
              >
                {startTotp.isPending ? "Ap prepare..." : "Aktive otantifikatè"}
              </Button>
            ) : (
              <div className="space-y-3">
                <p className="text-sm">Eskane kòd QR a, oswa antre kle a alamen nan aplikasyon an.</p>
                {totpSetup.qr && (
                  <img
                    src={totpSetup.qr}
                    alt="Kòd QR otantifikatè"
                    className="h-44 w-44 rounded-lg border border-border bg-white p-2"
                    data-testid="totp-qr"
                  />
                )}
                <p className="font-mono text-sm break-all" data-testid="totp-secret">{totpSetup.secret}</p>
                <div className="max-w-xs">
                  <Label htmlFor="totp-confirm-code">Kòd 6 chif pou konfime</Label>
                  <Input
                    id="totp-confirm-code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    placeholder="6 chif"
                    data-testid="totp-confirm-code"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    className="bg-brand-blue hover:bg-brand-blueHover text-white"
                    disabled={confirmTotp.isPending || totpCode.trim().length < 6}
                    onClick={() => confirmTotp.mutate()}
                    data-testid="totp-confirm-btn"
                  >
                    {confirmTotp.isPending ? "Ap verifye..." : "Konfime"}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setTotpSetup(null)}>
                    Anile
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <h2 className="font-heading text-lg font-semibold">{t("zelle_info")}</h2>
        <p className="text-xs text-muted-foreground mb-4">
          {t("zelle_info_sub")}
        </p>
        {zL ? <Skeleton className="h-40" /> : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("zelle_account_name")}</Label>
              <Input value={zForm.name || ""} onChange={(e) => setZForm({ ...zForm, name: e.target.value })} data-testid="zelle-name" />
            </div>
            <div>
              <Label>{t("zelle_handle")}</Label>
              <Input
                value={zelleHandle}
                onChange={(e) => setZelleHandle(e.target.value)}
                placeholder={t("zelle_handle_ph")}
                data-testid="zelle-handle"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{t("zelle_handle_hint")}</p>
            </div>
            <div className="sm:col-span-2">
              <Label>{t("zelle_whatsapp")}</Label>
              <Input
                value={zForm.whatsapp || ""}
                onChange={(e) => setZForm({ ...zForm, whatsapp: e.target.value })}
                placeholder="+509 …"
                data-testid="zelle-whatsapp"
              />
              <p className="text-[11px] text-muted-foreground mt-1">{t("zelle_whatsapp_hint")}</p>
            </div>
            <div className="sm:col-span-2"><Label>{t("instructions")}</Label><Textarea rows={3} value={zForm.instructions || ""} onChange={(e) => setZForm({ ...zForm, instructions: e.target.value })} data-testid="zelle-instructions" /></div>
            <div className="sm:col-span-2 flex flex-col items-end gap-2">
              <p className="text-[11px] text-muted-foreground text-right">
                Lè enfòmasyon an chanje, tout kliyan TGS resevwa yon mesaj WhatsApp otomatik. Yo pa bezwen reponn.
              </p>
              <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" onClick={() => saveZ.mutate()} disabled={saveZ.isPending} data-testid="save-zelle-btn">{t("save")}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-brand-blue" />
          <h2 className="font-heading text-lg font-semibold">Paramèt Sistèm</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Frè = {feePercent}% egzak. $800 → frè $80. Si frè soti nan total, kliyan resevwa $720. Si sender peye frè a, total la se $880.</p>
        {sL ? <Skeleton className="h-24" /> : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("service_fee")}</Label>
              <Input
                type="text"
                inputMode="decimal"
                lang="en"
                autoComplete="off"
                value={sForm.service_fee_percent}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!isDecimalTypingValue(v)) return;
                  setSForm({ ...sForm, service_fee_percent: v });
                }}
                placeholder="egz. 10"
                data-testid="fee-input"
              />
              <p className="text-[11px] text-muted-foreground mt-1">
                Frè = montan × pousantaj la. $800 → frè $80. Si frè a soti nan total, benefisyè jwenn $720. Si sender peye frè a, total la se $880.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label>URL sit piblik (tracking / resi WhatsApp)</Label>
              <Input
                value={sForm.public_base_url || ""}
                onChange={(e) => setSForm({ ...sForm, public_base_url: e.target.value })}
                placeholder="https://www.tgshaiti.com"
                data-testid="public-base-url-input"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Pa itilize URL preview Vercel. Domèn pwodiksyon: <span className="font-mono">https://www.tgshaiti.com</span>
              </p>
            </div>
            <div className="sm:col-span-2 flex justify-end">
              <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" onClick={() => saveS.mutate()} disabled={saveS.isPending} data-testid="save-system-settings-btn">{t("save")}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-brand-gold" />
          <h2 className="font-heading text-lg font-semibold">3 Bot Telegram — 3 secret key + Admin Chat ID</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Chak bot gen <strong>pwòp sekre</strong> (BotFather). Ajan yo <strong>pa bezwen</strong> Chat ID nan Settings —
          yo voye telefòn → PIN sou Bot Ajan, epi sistèm nan lyen chat yo otomatikman.
        </p>
        {sL ? <Skeleton className="h-64" /> : (
          <div className="space-y-4">
            <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-2" data-testid="bot-card-agent">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex w-7 h-7 rounded-full bg-brand-blue text-white items-center justify-center text-xs font-bold">1</span>
                <span className="font-heading font-semibold">Bot Ajan</span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-brand-blue bg-brand-blue/10 px-2 py-0.5 rounded">tag: ajan</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Flou: /start → nimewo telefòn → PIN 4 chif → meni (livrezon, retrè…). Pa gen Chat ID pou mete nan Settings.
              </p>
              <Label>Secret key Bot Ajan</Label>
              <Input type="password" autoComplete="off" value={sForm.agent_bot_token || ""} onChange={(e) => setSForm({ ...sForm, agent_bot_token: e.target.value })} placeholder="123456:AAH... (sekre #1)" data-testid="agent-bot-token-input" />
            </div>

            <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-2" data-testid="bot-card-notif">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex w-7 h-7 rounded-full bg-amber-500 text-slate-900 items-center justify-center text-xs font-bold">2</span>
                <span className="font-heading font-semibold">Bot Notifikasyon Admin</span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-600 bg-amber-500/10 px-2 py-0.5 rounded">tag: notif</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Voye alèt + <strong>foto pyès idantite</strong> bay admin apre chak livrezon (sou sit + nan chat).
              </p>
              <Label>Secret key Bot Notifikasyon</Label>
              <Input type="password" autoComplete="off" value={sForm.admin_notif_bot_token || ""} onChange={(e) => setSForm({ ...sForm, admin_notif_bot_token: e.target.value })} placeholder="123456:AAH... (sekre #2)" data-testid="admin-notif-token-input" />
            </div>

            <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-2" data-testid="bot-card-admin-tasks">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex w-7 h-7 rounded-full bg-emerald-600 text-white items-center justify-center text-xs font-bold">3</span>
                <span className="font-heading font-semibold">Bot Tach Admin</span>
                <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-600 bg-emerald-500/10 px-2 py-0.5 rounded">tag: tach-admin</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Manage transfè / retrè nan Telegram. Owner: Chat ID anba a.
                Team: ajoute manm nan paj <strong>Team</strong> (email + PIN) — yo /start → email → PIN.
                Apre kreye transfè, bot la jenere <strong>resi PDF</strong> + lyen WhatsApp benefisyè.
              </p>
              <Label>Secret key Bot Tach Admin</Label>
              <Input type="password" autoComplete="off" value={sForm.admin_mgmt_bot_token || ""} onChange={(e) => setSForm({ ...sForm, admin_mgmt_bot_token: e.target.value })} placeholder="123456:AAH... (sekre #3)" data-testid="admin-mgmt-token-input" />
            </div>

            <div className="rounded-xl border border-border p-4 space-y-2">
              <Label>Admin Chat ID(s) — owner (separe ak vigil)</Label>
              <Input value={sForm.admin_chat_ids || ""} onChange={(e) => setSForm({ ...sForm, admin_chat_ids: e.target.value })} placeholder="123456789" data-testid="admin-chat-ids-input" />
              <p className="text-xs text-muted-foreground">
                Pou Bot 2 (resevwa notifikasyon + foto) ak Bot 3 (aksè).
                Pa mete Chat ID ajan isit — ajan konekte ak telefòn + PIN.
              </p>
            </div>

            <div className="flex justify-between items-center pt-1 gap-3 flex-wrap">
              <Button
                variant="outline"
                onClick={() => testTg.mutate()}
                disabled={testTg.isPending || !sForm.admin_notif_bot_token || !sForm.admin_chat_ids}
                data-testid="test-telegram-btn"
                className="gap-2"
              >
                <Send className="h-4 w-4" /> {testTg.isPending ? "Ap voye..." : "Tès Bot Notifikasyon"}
              </Button>
              <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" onClick={() => saveS.mutate()} disabled={saveS.isPending} data-testid="save-settings-btn">{t("save")}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-brand-blue" />
          <h2 className="font-heading text-lg font-semibold">Imèl Notifikasyon (Resend)</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Resevwa imèl otomatik lè yon livrezon konfime oswa yon demann retrè fèt.
          Kreye yon API key gratis sou <code className="text-foreground bg-secondary px-1 rounded">resend.com</code>.
        </p>
        {sL ? <Skeleton className="h-48" /> : (
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <Label>Resend API Key</Label>
              <Input type="password" value={sForm.resend_api_key || ""} onChange={(e) => setSForm({ ...sForm, resend_api_key: e.target.value })} placeholder="re_..." data-testid="resend-api-key-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Imèl Ekspeditè (From)</Label>
              <Input value={sForm.resend_from_email || ""} onChange={(e) => setSForm({ ...sForm, resend_from_email: e.target.value })} placeholder="TGS <notifikasyon@domèn-ou.com>" data-testid="resend-from-input" />
              <p className="text-xs text-muted-foreground mt-1">Domèn nan dwe verifye nan Resend.</p>
            </div>
            <div className="sm:col-span-2">
              <Label>Imèl Destinatè — separe ak vigil</Label>
              <Input value={sForm.notify_emails || ""} onChange={(e) => setSForm({ ...sForm, notify_emails: e.target.value })} placeholder="admin@tgs.com, backup@tgs.com" data-testid="notify-emails-input" />
              <p className="text-xs text-muted-foreground mt-1">Adrès ki resevwa notifikasyon admin yo.</p>
            </div>
            <div className="sm:col-span-2 flex justify-between items-center pt-2 gap-3 flex-wrap">
              <Button variant="outline" onClick={() => testEmail.mutate()} disabled={testEmail.isPending || !sForm.resend_api_key || !sForm.resend_from_email || !sForm.notify_emails} data-testid="test-email-btn" className="gap-2">
                <Send className="h-4 w-4" /> {testEmail.isPending ? "Ap voye..." : "Voye Imèl Tès"}
              </Button>
              <Button className="bg-brand-blue hover:bg-brand-blueHover text-white" onClick={() => saveS.mutate()} disabled={saveS.isPending} data-testid="save-email-settings-btn">{t("save")}</Button>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="zones-settings-card">
        <div className="flex items-center gap-2">
          <MapPin className="h-5 w-5 text-brand-blue" />
          <h2 className="font-heading text-lg font-semibold">Zon / Adrès (WhatsApp kliyan)</h2>
        </div>
        <p className="text-xs text-muted-foreground mt-2 mb-4">
          Kliyan yo dwe chwazi yon zon anvan yo jwenn enfòmasyon Zelle.
          Mete zon + adrès presi isit (oswa nan meni <strong>Zon / Adrès</strong>).
        </p>
        <div className="flex flex-wrap gap-2">
          <a href="/admin/zones">
            <Button type="button" className="bg-brand-blue hover:bg-brand-blueHover text-white" data-testid="goto-zones-btn">
              Louvri Zon / Adrès
            </Button>
          </a>
          <a href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/zones/manage`} target="_blank" rel="noreferrer">
            <Button type="button" variant="outline" data-testid="goto-zones-api-btn">
              Jesyon zon (lyen dirèk API)
            </Button>
          </a>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6" data-testid="whatsapp-agent-settings">
        <div className="flex items-center gap-2">
          <MessageCircle className="h-5 w-5 text-emerald-600" />
          <h2 className="font-heading text-lg font-semibold">WhatsApp Bot Ajan</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          Bot WhatsApp pou <strong>ajan + kliyan</strong>. Lè admin kreye yon transfè,
          sistèm nan eseye <strong>voye resi a otomatikman</strong> sou WhatsApp{" "}
          <strong>moun ki voye</strong> a, epi tou sou <strong>benefisyè a</strong> si
          nimewo WhatsApp li sou transfè a (si bot la konekte). Si resi a echwe,
          Admin wè erè a epi ka eseye ankò.
        </p>
        <div className="mb-4">
          <a
            href={`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/transfers/create`}
            target="_blank"
            rel="noreferrer"
          >
            <Button type="button" variant="outline" data-testid="goto-transfer-create-api-btn">
              Kreye transfè (lyen dirèk — benefisyè WA opsyonèl)
            </Button>
          </a>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>URL sèvis WhatsApp</Label>
            <Input
              value={sForm.whatsapp_service_url || ""}
              onChange={(e) => setSForm({ ...sForm, whatsapp_service_url: e.target.value })}
              placeholder="Kite vid sou Render (entegre)"
              data-testid="wa-service-url"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Pa mete api.tgshaiti.com — kite vid si WhatsApp entegre ak backend.
            </p>
          </div>
          <div className="sm:col-span-2">
            <Label>Bridge secret (opsyonèl)</Label>
            <Input
              type="password"
              value={sForm.whatsapp_bridge_secret || ""}
              onChange={(e) => setSForm({ ...sForm, whatsapp_bridge_secret: e.target.value })}
              placeholder="Menm valè ak WHATSAPP_BRIDGE_SECRET si ou genyen"
              data-testid="wa-bridge-secret"
            />
          </div>
          <div className="sm:col-span-2 flex flex-wrap gap-2">
            <Button
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              disabled={saveWa.isPending}
              onClick={() => saveWa.mutate()}
              data-testid="wa-activate-btn"
            >
              {saveWa.isPending ? "Ap aktive..." : "Aktive WhatsApp Ajan"}
            </Button>
            <Button variant="outline" onClick={() => refetchWa()} data-testid="wa-refresh-btn">
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
            <Button variant="outline" onClick={() => waLogout.mutate()} disabled={waLogout.isPending} data-testid="wa-logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Dekonekte / Nouvo QR
            </Button>
          </div>
          <div className="sm:col-span-2 text-sm rounded-xl border border-border bg-secondary/20 p-3 space-y-1" data-testid="wa-status-box">
            <p>
              Estati:{" "}
              <strong>
                {waStatus?.connected
                  ? "Konekte"
                  : waStatus?.state === "qr"
                    ? "Ap tann eskane QR"
                    : waStatus?.state || "pa disponib"}
              </strong>
            </p>
            {waStatus?.me && <p className="text-xs font-mono text-muted-foreground">{waStatus.me}</p>}
            {waStatus?.auth && (
              <p className="text-xs text-muted-foreground">
                Auth: {waStatus.auth}
                {waStatus.auth_persistent || waStatus.auth === "mongo"
                  ? " — sove nan MongoDB (rete apre redeploy)"
                  : " — pa persistan"}
                {typeof waStatus.auth_key_count === "number"
                  ? ` · ${waStatus.auth_key_count} kle`
                  : ""}
              </p>
            )}
            {waStatus?.auth_creds_saved === false && waStatus?.connected && (
              <p className="text-xs text-amber-600">Atansyon: kredansyèl poko verifye nan Mongo.</p>
            )}
            {waStatus?.qr && (
              <div className="pt-2">
                <img src={waStatus.qr} alt="WhatsApp QR" className="w-56 h-56 bg-white rounded-lg border" />
                <p className="text-xs text-muted-foreground mt-2">
                  WhatsApp → Aparèy ki konekte → Konekte yon aparèy → eskane.
                </p>
              </div>
            )}
          </div>
          <div>
            <Label>Tès voye (nimewo)</Label>
            <Input value={waTestTo} onChange={(e) => setWaTestTo(e.target.value)} placeholder="+509…" data-testid="wa-test-to" />
          </div>
          <div className="flex items-end">
            <Button
              variant="outline"
              disabled={waTest.isPending || !waTestTo.trim() || !waStatus?.connected}
              onClick={() => waTest.mutate()}
              data-testid="wa-test-send-btn"
            >
              {waTest.isPending ? "Ap voye..." : "Voye mesaj tès"}
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6">
        <div className="flex items-center gap-2 mb-2">
          <Bot className="h-4 w-4 text-brand-gold" />
          <h3 className="font-heading font-semibold text-base">Ki jan pou konfigire (san webhook)</h3>
        </div>
        <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1.5">
          <li>@BotFather → kreye <strong>3 bot</strong> (Ajan / Notifikasyon / Tach Admin) → 3 secret key diferan.</li>
          <li>Kole 3 sekre yo + Admin Chat ID → Sove → Tès Bot Notifikasyon.</li>
          <li>Kreye ajan ak telefòn + PIN (pa bezwen Chat ID).</li>
          <li>Ajan: /start sou Bot Ajan → nimewo → PIN → meni.</li>
          <li>Team: paj Team → ajoute email + PIN → Bot Tach Admin /start → email → PIN → meni.</li>
          <li>Bot Tach Admin kreye transfè → resi PDF + bouton WhatsApp (kliyan voye → kliyan resevwa).</li>
          <li>Apre livrezon: foto pyès la sove sou sit la epi voye nan chat admin (Bot 2).</li>
        </ol>
      </div>

      <ClientBroadcastDialog open={clientMsgOpen} onClose={() => setClientMsgOpen(false)} />
    </div>
  );
}

function ClientBroadcastDialog({ open, onClose }) {
  const [text, setText] = useState("");
  const [autoNote, setAutoNote] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [result, setResult] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ["whatsapp-clients"],
    queryFn: async () => (await api.get("/whatsapp/clients")).data,
    enabled: open,
  });
  const clients = useMemo(() => data?.clients || [], [data]);
  const clientKey = clients.map((c) => c.phone).join("|");

  useEffect(() => {
    if (!open) return;
    setText("");
    setAutoNote(true);
    setSearch("");
    setResult(null);
  }, [open]);

  useEffect(() => {
    if (!open || !data?.clients?.length) return;
    setSelected(new Set(data.clients.map((c) => c.phone).filter(Boolean)));
  }, [open, clientKey, data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => (
      (c.name || "").toLowerCase().includes(q)
      || (c.phone || "").toLowerCase().includes(q)
    ));
  }, [clients, search]);

  const sendMut = useMutation({
    mutationFn: async () => (await api.post("/whatsapp/broadcast-clients", {
      text: text.trim(),
      phones: Array.from(selected),
      auto_note: autoNote,
    })).data,
    onSuccess: (payload) => {
      setResult(payload);
      toast.success(`Mesaj ale bay ${payload.sent || 0} kliyan`);
    },
    onError: (err) => toast.error(formatApiError(err.response?.data?.detail)),
  });

  const togglePhone = (phone, checked) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(phone);
      else next.delete(phone);
      return next;
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !sendMut.isPending) onClose();
      }}
    >
      <DialogContent className="bg-card max-w-lg" data-testid="client-broadcast-dialog">
        <DialogHeader>
          <DialogTitle>Ekri kliyan TGS</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-muted-foreground">
          Chwazi tout kliyan yo, oswa yon pati. Nimewo yo soti nan WhatsApp moun ki voye transfè yo.
        </p>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Chèche non oswa nimewo"
          data-testid="client-broadcast-search"
        />
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className="text-muted-foreground">
            {isLoading ? "Ap chaje kliyan…" : `${selected.size} / ${clients.length} chwazi`}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSelected(new Set(clients.map((c) => c.phone).filter(Boolean)))}
              data-testid="client-broadcast-all"
            >
              Tout
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setSelected(new Set())}
              data-testid="client-broadcast-none"
            >
              Okenn
            </Button>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-secondary/30 p-3 max-h-40 overflow-y-auto text-xs space-y-2">
          {!isLoading && filtered.length === 0 && (
            <p className="text-muted-foreground">Pa gen kliyan ak WhatsApp pou kounye a.</p>
          )}
          {filtered.map((c) => (
            <label key={c.phone} className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={selected.has(c.phone)}
                onCheckedChange={(v) => togglePhone(c.phone, !!v)}
                data-testid={`client-pick-${c.phone}`}
              />
              <span className="truncate flex-1">{c.name || "San non"}</span>
              <span className="font-mono shrink-0">{c.phone}</span>
            </label>
          ))}
        </div>
        <div className="space-y-2">
          <Label>Mesaj</Label>
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={5}
            maxLength={3500}
            placeholder="Ekri mesaj ou a pou kliyan yo…"
            data-testid="client-broadcast-text"
          />
          <p className="text-[11px] text-muted-foreground text-right">{text.trim().length}/3500</p>
        </div>
        <label className="flex items-start gap-2 text-sm cursor-pointer">
          <Checkbox
            checked={autoNote}
            onCheckedChange={(v) => setAutoNote(!!v)}
            data-testid="client-broadcast-auto-note"
          />
          <span>Ajoute nòt mesaj otomatik — yo pa bezwen reponn</span>
        </label>
        {result && (
          <div className="rounded-xl border border-border p-3 text-xs space-y-1" data-testid="client-broadcast-result">
            <p>
              Rezilta: <strong>{result.sent}</strong> ale · <strong>{result.failed}</strong> echwe · total {result.total}
            </p>
            {(result.results || [])
              .filter((r) => !r.ok)
              .slice(0, 8)
              .map((r) => (
                <p key={`${r.to}-${r.name}`} className="text-rose-600">
                  {r.name || r.to}: {r.detail}
                </p>
              ))}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={sendMut.isPending}>
            Fèmen
          </Button>
          <Button
            type="button"
            className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
            onClick={() => sendMut.mutate()}
            disabled={sendMut.isPending || !text.trim() || selected.size === 0}
            data-testid="client-broadcast-send"
          >
            <MessageCircle className="h-4 w-4" />
            {sendMut.isPending ? "Ap voye…" : `Voye bay ${selected.size} kliyan`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
